import { courses, directions, questions, reasonPriority } from './config.js'

export function selectedOptions(answers) {
  return questions.flatMap((question) => {
    const selected = Array.isArray(answers[question.id]) ? answers[question.id] : [answers[question.id]].filter(Boolean)
    return selected.map((id) => ({ question, option: question.options.find((item) => item.id === id) })).filter((item) => item.option)
  })
}

export function scoreAnswers(answers) {
  const scores = Object.fromEntries(directions.map((direction) => [direction, 0]))
  for (const { option } of selectedOptions(answers)) {
    for (const [direction, weight] of Object.entries(option.scores || {})) scores[direction] += weight
  }
  return scores
}

export function detectDirection(answers) {
  const scores = scoreAnswers(answers)
  const ordered = directions.map((id) => ({ id, score: scores[id] })).sort((a, b) => b.score - a.score || directions.indexOf(a.id) - directions.indexOf(b.id))
  return { direction: ordered[0].id, scores, ordered }
}

export function detectLearningDepth(answers) {
  const mapping = { career: 'career', focused: 'focused', explore: 'workshop', weekend: 'weekend' }
  return mapping[answers.depth] || 'workshop'
}

function tagsFor(answers) {
  return new Set(selectedOptions(answers).flatMap(({ option }) => option.tags || []))
}

function courseFor(direction, level, scores) {
  const isUi = direction === 'uiux' || direction === 'ui'
  const isWeb = direction === 'web'
  const isSocial = direction === 'social'
  const isCreative = direction === 'creative'
  if (level === 'career') {
    const uiStrength = scores.uiux + scores.ui * 0.45
    const creativeWebStrength = scores.web + scores.creative * 0.65 + scores.social * 0.35
    return uiStrength >= creativeWebStrength ? courses.uiux : courses.creativeWeb
  }
  if (level === 'focused') return isWeb ? courses.nocode : isCreative || isSocial ? courses.socialAds : courses.figma
  if (level === 'weekend') return isWeb ? courses.weekendNocode : isCreative || isSocial ? courses.weekendSocial : courses.weekendFigma
  if (direction === 'ai') return courses.prompt
  if (isWeb) return courses.wordpress
  if (isCreative || isSocial) return courses.photoshop
  return courses.figmaStarter
}

export function selectPrimaryRecommendation(answers) {
  const { direction, scores, ordered } = detectDirection(answers)
  const level = detectLearningDepth(answers)
  return { course: courseFor(direction, level, scores), direction, level, scores, ordered }
}

export function selectSecondaryRecommendation(answers, primary = selectPrimaryRecommendation(answers)) {
  let level
  let label
  if (primary.level === 'career') {
    level = 'focused'
    label = 'focused'
  } else if (primary.level === 'focused') {
    level = 'career'
    label = 'deeper'
  } else if (primary.level === 'workshop') {
    level = 'focused'
    label = 'deeper'
  } else {
    level = 'career'
    label = 'deeper'
  }
  const course = courseFor(primary.direction, level, primary.scores)
  return { course: course.id === primary.course.id ? courseFor(primary.ordered[1]?.id || 'ui', level, primary.scores) : course, label }
}

export function selectReasonKeys(answers) {
  const tags = tagsFor(answers)
  const present = new Set(['preferredProject', 'workStyle', 'learningDepth'])
  if (tags.has('career') || tags.has('freelance') || tags.has('recognition') || tags.has('explore')) present.add('futureGoal')
  if (answers.enjoy?.length) present.add('naturalInterest')
  if (tags.has('ai')) present.add('aiInterest')
  if (tags.has('practical')) present.add('practical')
  if (tags.has('mentor')) present.add('mentorship')
  if (tags.has('malayalam')) present.add('languageSupport')
  if (tags.has('beginner')) present.add('beginnerReady')
  return reasonPriority.filter((key) => present.has(key)).slice(0, 5)
}

export function buildRecommendation(answers) {
  const primary = selectPrimaryRecommendation(answers)
  return { ...primary, secondary: selectSecondaryRecommendation(answers, primary), reasonKeys: selectReasonKeys(answers), beginnerNote: ['guidance', 'beginning'].includes(answers.computer) }
}
